class AnnotatedFeatures extends HTMLElement {
  connectedCallback() {
    this.id = this.dataset.featureId;
    this.items = this.querySelectorAll('.annotated-feature');
    this.image = this.querySelector('.featured-image');
    this.imageWrapper = this.querySelector('.featured-image-wrapper');
    this.originalWidth = this.dataset.imageWidth;
    this.originalHeight = this.dataset.imageHeight;
    this.currentWidth = this.originalWidth;
    this.currentHeight = this.originalHeight;
    this.xMultiplier = 1;
    this.yMultiplier = 1;
    this.circleWidth = 30;
    waitForImageToLoad(this.image).then(() => {
      this.setDimensions();
      window.addEventListener('resize', debounce(() => {
        this.setDimensions();
      }, 300))
    })
  }

  getCircleWidth() {
    return this.isMobile() ? 20 : 30;
  }

  setDimensions() {
    this.circleWidth = this.getCircleWidth();
    const dimensions = this.image.getBoundingClientRect();
    this.currentWidth = dimensions.width;
    this.currentHeight = dimensions.height;
    this.xMultiplier = parseFloat(this.currentWidth) / parseFloat(this.originalWidth);
    this.yMultiplier = parseFloat(this.currentHeight) / parseFloat(this.originalHeight);
    gsap.set(this.image, {
      opacity: 0
    });
    this.image.classList.remove('opacity-0');
    gsap.to(this.image, {
      opacity: 1,
      duration: 0.5,
      scrollTrigger: this.image
    })
    this.items.forEach(i => {
      this.setupItemPosition(i);
    })
  }

  setupItemPosition(item) {
    const xPos = parseFloat(this.xMultiplier) * parseFloat(item.dataset.xPos);
    const yPos = parseFloat(this.yMultiplier) * parseFloat(item.dataset.yPos);
    gsap.set(item, {
      opacity: 0,
      position: 'absolute',
      top: `${yPos + 10}px`,
      left: 0,
      width: '100%',
      xPercent: 0,
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderColor: 'var(--accent-color)',
      padding: 0
    });
    this.createAnchor(item, {
      yPos,
      xPos
    })
  }

  createAnchor(item, props) {
    const itemId = item.dataset.featureId;
    const oldSvg = this.imageWrapper.querySelector(`svg[data-feature-id="${itemId}"]`);
    if(oldSvg) oldSvg.remove();
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.position = 'absolute';
    svg.style.top = `${props.yPos}px`;
    svg.style.left = `${props.xPos}px`;
    svg.setAttribute('data-feature-id', itemId);
    svg.style.color = 'var(--accent-color)';
    svg.style.overflow ='visible';
    svg.classList.add('z-pop');
    svg.classList.add('feature-svg');
    // svg.style.opacity = 0;
    this.imageWrapper.append(svg);
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('r', this.circleWidth);
    circle.setAttribute('cx', this.circleWidth);
    circle.setAttribute('cy', this.circleWidth);
    circle.style.fill = 'transparent';
    circle.style.stroke = 'currentColor';
    circle.style.strokeWidth = '3px';
    svg.append(circle);
    if(this.isMobile()) {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', this.circleWidth);
      line.setAttribute('y1', 0);
      line.setAttribute('x2', this.circleWidth);
      line.setAttribute('y2', '-50');
      line.style.fill = 'none';
      line.style.opacity = 0;
      line.style.stroke = 'currentColor';
      line.style.strokeLinecap = 'round';
      line.style.strokeLinejoin = 'round';
      line.style.strokeWidth = '3px';
      svg.append(line);
      svg.classList.add('animate-bounce')
      gsap.set(item, {
        width: '80%',
        left: '50%',
        xPercent: -50,
        borderWidth: '3px',
        backgroundColor: 'var(--color-scheme-accent-two-background)',
        padding: '12px',
        zIndex: 2
      })
      const itemDimensions = item.getBoundingClientRect();
      gsap.set(item, {
        top: `${(props.yPos - itemDimensions.height - 50)}px`,
        left: props.xPos < itemDimensions.x ? props.xPos : '50%',
        xPercent: props.xPos < itemDimensions.x ? 0 : -50
      })
      circle.addEventListener('click', (i) => {
        const el = i.currentTarget.closest('svg');
        if(el.classList.contains('animate-bounce')) {
          this.querySelectorAll('.feature-svg').forEach(i => {
            if(!i.classList.contains('animate-bounce')) {
              gsap.timeline()
                .to(`.annotated-feature[data-feature-id="${i.dataset.featureId}"]`, {
                  opacity: 0,
                  duration: 0.5,
                  zIndex: 2,
                  onComplete: (svg) => svg.classList.add('animate-bounce'),
                  onCompleteParams: [i]
                })
                .to(i.querySelector('line'), {
                  opacity: 0,
                  duration: 0.2
                }, 0)
            }
          })
          el.classList.remove('animate-bounce');
          gsap.timeline()
            .set(el.querySelector('line'), {
              opacity: 1
            })
            .from(el.querySelector('line'), {
              drawSVG: 0,
              duration: 0.3
            })
            .to(item, {
              opacity: 1,
              duration: 0.5,
              zIndex: 4
            })
        }
        else {
          gsap.timeline()
            .to(item, {
              opacity: 0,
              duration: 0.5,
              zIndex: 2,
              onComplete: (svg) => svg.classList.add('animate-bounce'),
              onCompleteParams: [el]
            })
            .to(el.querySelector('line'), {
              opacity: 0,
              duration: 0.2
            }, 0)
        }
      });
    }
    else {
      const itemHeading = item.querySelector('.featured-heading');
      const headingDimensions = itemHeading.getBoundingClientRect();
      const svgDimensions = svg.getBoundingClientRect();
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      const leftPositioned = headingDimensions.right < svgDimensions.left;
      const lineWidth = leftPositioned
        ? Math.abs(svgDimensions.left - (headingDimensions.right + 10))
        : Math.abs(svgDimensions.left - (headingDimensions.left - 10));
      line.setAttribute('x1', leftPositioned ? 0 : `${(this.circleWidth * 2)}`);
      line.setAttribute('y1', this.circleWidth);
      line.setAttribute('x2', leftPositioned ? `-${lineWidth}` : `${lineWidth}`);
      line.setAttribute('y2', this.circleWidth);
      line.style.fill = 'none';
      line.style.stroke = 'currentColor';
      line.style.strokeLinecap = 'round';
      line.style.strokeLinejoin = 'round';
      line.style.strokeWidth = '3px';
      svg.append(line);

      gsap.timeline({
        scrollTrigger: this.image
      })
      .from(circle, {
        drawSVG: 0,
        duration: 0.5
      })
      .from(line, {
        drawSVG: 0,
        duration: 0.5
      })
      .to(item, {
        opacity: 1,
        duration: 0.5
      })
    }
  }

  isMobile() {
    return window.innerWidth < 1024;
  }
}

customElements.define('annotated-features', AnnotatedFeatures)